import { forwardRef, PropsWithChildren, ReactNode, useState } from 'react';

import styled from '@emotion/styled';
import { addSeconds } from 'date-fns/addSeconds';
import { intervalToDuration } from 'date-fns/intervalToDuration';
import { useTranslations } from 'next-intl';
import { useInterval } from 'usehooks-ts';

import { ThemeColorKey } from '@web/ui/theme/type';
import { getTextStroke } from '@web/ui/theme/utils';

import { TVFrame } from './TvFrame';

const formatTime = (seconds: number) => {
  const i = intervalToDuration({
    start: new Date(),
    end: addSeconds(new Date(), seconds),
  });

  return `${String(i.hours || 0).padStart(2, '0')}:${String(i.minutes || 0).padStart(2, '0')}:${String(i.seconds || 0).padStart(2, '0')}`;
};

const Container = styled.div`
  overflow: hidden;
  position: fixed;
  inset: 0;
  z-index: 998;
  background-color: ${({ theme }) => theme.color.inverseSurface};

  > svg {
    position: absolute;
    height: 100%;
    width: 100%;
    color: ${({ theme }) => theme.color.inverseSurface};
    ${({ theme }) => theme.breakpoints.down('sm')} {
      display: none;
    }

    * {
      transition: all 0.8s ease-in-out;
    }
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const TVSlot = styled.div`
  position: absolute;
  inset: 0;
`;

const TopLeftContainer = styled.div<{ opaque: boolean }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('0250')};
  opacity: ${({ theme, opaque }) => (opaque ? theme.opacity.dense : 0)};
  transition: opacity ease-in-out 0.8s;

  > span {
    color: ${({ theme }) => theme.color.background};
    ${getTextStroke('1', 'inverseSurface')}
    font: ${({ theme }) => theme.typography.body.regular};
    font-size: ${({ theme }) => theme.typescale.size['2']};
    line-height: ${({ theme }) => theme.typescale.lineHeight.default};
  }

  top: 8%;
  left: 4%;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    top: 16px;
    left: 16px;
  }
`;

const copyrightColor = {
  light: 'onBackground',
  dark: 'surface',
} as const;
const Copyright = styled.span<{ color: ThemeColorKey }>`
  position: absolute;
  width: 100%;
  text-align: center;

  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  color: ${({ theme, color }) => theme.color[color]};
  line-height: ${({ theme }) => theme.typescale.lineHeight.default};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.default};

  transition: color 0.8s ease-in-out;

  bottom: 48px;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    bottom: 16px;
  }
`;

interface IProps {
  variant: 'light' | 'dark';
  hideTapeInfo?: boolean;
  tvContent?: ReactNode;
}

export const SplashLayout = forwardRef<
  HTMLDivElement,
  PropsWithChildren<IProps>
>(({ children, variant, hideTapeInfo, tvContent }, ref) => {
  const t = useTranslations();

  const [seconds, setSeconds] = useState(0);
  useInterval(
    () => {
      setSeconds((s) => s + 1);
    },
    tvContent ? 1000 : null,
  );

  return (
    <Container ref={ref}>
      {tvContent ? <TVSlot>{tvContent}</TVSlot> : null}
      <TVFrame variant={variant} />
      <TopLeftContainer opaque={!hideTapeInfo}>
        <span>TAPE {tvContent ? 'A' : '//'}</span>
        <span>{tvContent ? 'PAUSE' : 'PLAY'}</span>
        <span>{formatTime(seconds)}</span>
      </TopLeftContainer>
      <Content>{children}</Content>
      <Copyright color={copyrightColor[variant]}>
        {t('PERSONA_COPYRIGHT')}
      </Copyright>
    </Container>
  );
});
